import { Button, Input, Modal, Select } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useExerciseFormatsSelector,
  useMasterSkillTypesSelector,
  useQuestionEditSelector,
} from '../../../redux/selector';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'lodash';
import {
  closeEditQuestionModal,
  showEditQuestionModal,
  updateEditQuestion,
  updateQuestionProcess,
} from '../../../redux/slice/editQuestionModalSlice';
import CkEditorWord4 from 'component/common/CkEditorWord4/CkEditorWord4';
import { CK_EDITOR_BASE_CONFIG } from 'common/constant';
import { QuestionType } from 'common/enum';
import { questionTypes, thinkingLevels } from 'common/data';
import { useParams } from 'react-router-dom';
import EditOneChoiceQuestion from '../EditOneChoiceQuestion';
import EditYesNoQuestion from '../EditYesNoQuestion';
import EditShortQuestion from '../EditShortQuestion';
import { AppDispatch } from 'redux/store/store';
import { QuestionRequest } from 'model/request';
import { setLoading } from '../../../redux/slice/appConfigSlice';
import { unwrapResult } from '@reduxjs/toolkit';

const config = {
  ...CK_EDITOR_BASE_CONFIG,
  height: 200,
  extraPlugins: 'niw_btn,ckeditor_wiris',
  allowedContent: true,
  font_defaultLabel: 'Arial',
  fontSize_defaultLabel: 16,
  contentsCss: ["body {font-size: 18px; font-family: 'Arial';word-wrap:break-word;}"],
  actionFocus: false,
};

interface EditQuestionPopupProps {
  onBeforeSuccess?: () => Promise<void>;
}

export default function EditQuestionPopup({ onBeforeSuccess }: EditQuestionPopupProps) {
  const param = useParams();
  const exerciseFormats = useExerciseFormatsSelector();
  const skillTypes = useMasterSkillTypesSelector();
  const dispatch = useDispatch<AppDispatch>();
  const { visible, question } = useQuestionEditSelector();
  const editQuestionRef = useRef<QuestionRequest | null | undefined>(question);
  const [editQuestionState, setEditQuestionState] = useState<QuestionRequest | null | undefined>(question);

  const onChangeInput = function (e: any, fieldName: string) {
    if (!editQuestionState) return;
    const questionTemp: any = cloneDeep(editQuestionState);
    questionTemp[fieldName] = e.target.value;

    dispatch(updateEditQuestion(questionTemp));
  };

  const onChangeCkeditor = (content: string, filedName: string) => {
    if (!editQuestionRef.current) return;
    const editQuestionTemp = { ...editQuestionRef.current } as any;
    editQuestionTemp[filedName] = content;

    dispatch(updateEditQuestion(editQuestionTemp));
  };

  const onChangeSelect = function (e: any, fieldName: string) {
    const editQuestionTemp = { ...editQuestionState } as any;

    switch (fieldName) {
      case 'exerciseFormats':
      case 'skillTypes':
        editQuestionTemp[fieldName] = e.map((item: any) => ({
          id: item,
        }));
        break;
      default:
        editQuestionTemp[fieldName] = e;
        break;
    }

    dispatch(updateEditQuestion(editQuestionTemp));
  };

  const handleChangeQuestionDetails = useCallback(
    (data: any, rightAnswer: string | number | undefined | null, questionType: QuestionType) => {
      const questionEditTemp = { ...editQuestionRef.current } as QuestionRequest;
      switch (questionType) {
        case QuestionType.ONE_CHOICE_QUESTION:
          if (data) {
            questionEditTemp.questionDetails = data;
          } else if (rightAnswer !== null && rightAnswer !== undefined) {
            questionEditTemp.rightAnswer = rightAnswer + '';
          }
          break;
        case QuestionType.YES_NO_QUESTION:
        case QuestionType.SHORT_QUESTION:
          questionEditTemp.rightAnswer = rightAnswer + '';
          break;
        default:
          break;
      }

      dispatch(updateEditQuestion(questionEditTemp));
    },
    [dispatch],
  );

  const getExerciseFormat = useMemo(() => {
    return editQuestionState?.exerciseFormats ? editQuestionState?.exerciseFormats.map((item) => item.id) : [];
  }, [editQuestionState?.exerciseFormats]);

  const getSkillType = useMemo(() => {
    return editQuestionRef.current && editQuestionRef.current.skillTypes
      ? editQuestionRef.current.skillTypes.map((item) => item.id)
      : [];
  }, [editQuestionState?.skillTypes]);

  const getOptionExerciseFormats = useMemo(() => {
    return exerciseFormats
      .filter((item) => {
        if (param.subjectId) {
          return item.subject.id === param.subjectId;
        } else return true;
      })
      .map((item) => ({
        value: item.id,
        label: `${item.code}-${item.name}`,
      }));
  }, [exerciseFormats, param.subjectId]);

  const getOptionSkillTypes = useMemo(() => {
    return skillTypes
      .filter((item) => {
        if (param.subjectId) {
          return item.subject.id === param.subjectId;
        } else return true;
      })
      .map((item) => ({
        value: item.id,
        label: `${item.code}-${item.name}`,
      }));
  }, [skillTypes, param.subjectId]);

  const editQuestionDetails = useMemo(() => {
    if (!editQuestionRef.current) return null;
    switch (editQuestionRef.current.type) {
      case QuestionType.ONE_CHOICE_QUESTION:
        return (
          <EditOneChoiceQuestion
            questionDetails={editQuestionState?.questionDetails}
            commitChange={handleChangeQuestionDetails}
            rightAnswer={editQuestionRef.current.rightAnswer}
          />
        );
      case QuestionType.YES_NO_QUESTION:
        return (
          <EditYesNoQuestion
            commitChange={handleChangeQuestionDetails}
            rightAnswer={editQuestionRef.current.rightAnswer}
          />
        );
      case QuestionType.SHORT_QUESTION:
      case QuestionType.TOPIC_QUESTION:
        return (
          <EditShortQuestion
            commitChange={handleChangeQuestionDetails}
            rightAnswer={editQuestionRef.current.rightAnswer}
          />
        );
    }
    return null;
  }, [
    editQuestionState?.questionDetails,
    editQuestionState?.rightAnswer,
    editQuestionState?.type,
    handleChangeQuestionDetails,
  ]);

  useEffect(() => {
    editQuestionRef.current = question;
    if (visible) {
      dispatch(setLoading(true));
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 1500);
    }
  }, [visible]);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(showEditQuestionModal());
    const timeoutId = setTimeout(() => {
      dispatch(closeEditQuestionModal());
      dispatch(setLoading(false));
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      dispatch(setLoading(false));
    };
  }, []);

  useEffect(() => {
    editQuestionRef.current = question;
    setEditQuestionState(question);
  }, [question]);

  function clearModal() {
    dispatch(closeEditQuestionModal());
  }

  async function handleSave() {
    await dispatch(updateQuestionProcess()).then(unwrapResult);
    onBeforeSuccess && (await onBeforeSuccess());
  }

  return (
    <Modal
      open={visible}
      onOk={handleSave}
      onCancel={clearModal}
      width={600}
      zIndex={1000}
      footer={[
        <Button key="back" onClick={clearModal}>
          Hủy
        </Button>,
        <Button key="submit" type="primary" onClick={handleSave}>
          Lưu
        </Button>,
      ]}
    >
      <div className="w-100 p-4 w-full">
        <div className={'flex items-center mt-2'}>
          <div className="text-[18px] w-[30%] text-left">Mã câu hỏi:</div>
          <Input
            placeholder="Mã câu hỏi"
            name="point"
            className=" w-fit"
            value={editQuestionState ? editQuestionState.code : ''}
            onChange={(e) => onChangeInput(e, 'code')}
          />
        </div>
        <div className="items-center mt-2">
          <div className="text-[18px] w-[30%] text-left">Đề bài:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={editQuestionState?.topic ?? ''}
              keyId={editQuestionState?.id}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'topic');
              }}
            />
          </div>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[30%] text-left">Chọn loại câu hỏi:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              onChange={(e) => onChangeSelect(e, 'type')}
              className="w-full"
              placeholder="Loại câu hỏi"
              style={{ flex: 1 }}
              disabled={editQuestionState?.type === QuestionType.GROUPED_QUESTION}
              value={editQuestionState?.type}
              options={questionTypes.map((ques) => ({
                value: ques.key,
                label: ques.value,
              }))}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' + (editQuestionState?.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[30%] text-left">Dạng bài tập:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'exerciseFormats')}
              className="w-full"
              placeholder="Dạng bài tập"
              style={{ flex: 1 }}
              value={getExerciseFormat}
              options={getOptionExerciseFormats}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' + (editQuestionState?.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[30%] text-left">Năng lực:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'skillTypes')}
              className="w-full"
              placeholder="Năng lực"
              style={{ flex: 1 }}
              value={getSkillType}
              options={getOptionSkillTypes}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' + (editQuestionState?.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[30%] text-left">Mức độ:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'thinkingLevels')}
              className="w-full"
              placeholder="Mức độ"
              style={{ flex: 1 }}
              value={editQuestionState?.thinkingLevels}
              options={thinkingLevels.map((thinkLevel) => ({
                value: thinkLevel.key,
                label: `${thinkLevel.code}-${thinkLevel.value}`,
              }))}
            />
          </div>
        </div>
        <div
          className={
            'items-center mt-2 w-full ' + (editQuestionState?.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Câu trả lời:</div>
          {editQuestionDetails}
        </div>
        <div
          className={'items-center mt-2 ' + (editQuestionState?.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')}
        >
          <div className="text-[18px] text-left">Gợi ý, hướng dẫn giải:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={editQuestionState?.hint ?? ''}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'hint');
              }}
              keyId={editQuestionState?.id}
            />
          </div>
        </div>
        <div className="items-center mt-2">
          <div className="text-[18px] text-left">Lời giải chi tiết:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={editQuestionState?.solutionGuide ?? ''}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'solutionGuide');
              }}
              keyId={editQuestionState?.id}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
